class CacheManager {
  constructor(dbName = 'gift', storeName = 'blobs') {
    this.dbName = dbName;
    this.storeName = storeName;
    this.db = null;
    this.dbInitialized = false;
    this.memoryCache = new Map();
    this.useIndexedDB = true;

    // 初始化缓存系统
    this.init();
  }

  /**
   * 初始化缓存系统
   */
  async init() {
    if (this.isIndexedDBSupported()) {
      try {
        await this.initDB();
      } catch (error) {
        console.warn('IndexedDB initialization failed, falling back to memory cache:', error);
        this.useIndexedDB = false;
      }
    } else {
      console.warn('IndexedDB not supported, using memory cache only');
      this.useIndexedDB = false;
    }
  }

  /**
   * 初始化 IndexedDB
   * @returns {Promise}
   */
  initDB() {
    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.dbName, 1);

        request.onerror = (event) => {
          this.dbInitialized = false;
          reject(new Error('Failed to open IndexedDB: ' + event.target.error));
        };

        request.onsuccess = (event) => {
          this.db = event.target.result;
          this.dbInitialized = true;
          resolve(this.db);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: 'key' });
          }
        };
      } catch (error) {
        this.dbInitialized = false;
        reject(error);
      }
    });
  }

  /**
   * 检查浏览器是否支持 IndexedDB
   * @returns {boolean}
   */
  isIndexedDBSupported() {
    try {
      return typeof window !== 'undefined' && 'indexedDB' in window && indexedDB !== undefined;
    } catch (e) {
      return false;
    }
  }

  /**
   * 设置缓存项
   * @param {string} key
   * @param {any} value
   * @returns {Promise}
   */
  async set(key, value) {
    // 总是先设置到内存缓存中，保证快速访问
    this.memoryCache.set(key, value);

    // 如果使用 IndexedDB，则也存储到 IndexedDB 中
    if (this.useIndexedDB && this.dbInitialized && this.db) {
      try {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          const request = store.put({ key, value });

          request.onsuccess = () => {
            resolve();
          };

          request.onerror = (event) => {
            console.warn('Failed to set data in IndexedDB:', event.target.error);
            resolve(); // 即使 IndexedDB 失败也 resolve，因为内存缓存已设置
          };
        });
      } catch (error) {
        console.warn('Error setting data in IndexedDB:', error);
        // 即使 IndexedDB 出错也返回 resolve，因为内存缓存已设置
        return Promise.resolve();
      }
    }

    return Promise.resolve();
  }

  /**
   * 获取缓存项
   * @param {string} key
   * @returns {Promise}
   */
  async get(key) {
    // 首先从内存缓存中获取
    if (this.memoryCache.has(key)) {
      return Promise.resolve(this.memoryCache.get(key));
    }

    // 如果使用 IndexedDB，尝试从 IndexedDB 中获取
    if (this.useIndexedDB && this.dbInitialized && this.db) {
      try {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.storeName], 'readonly');
          const store = transaction.objectStore(this.storeName);
          const request = store.get(key);

          request.onsuccess = () => {
            if (request.result) {
              // 同步到内存缓存中，提高下次访问速度
              this.memoryCache.set(key, request.result.value);
              resolve(request.result.value);
            } else {
              resolve(undefined);
            }
          };

          request.onerror = (event) => {
            console.warn('Failed to get data from IndexedDB:', event.target.error);
            resolve(undefined);
          };
        });
      } catch (error) {
        console.warn('Error getting data from IndexedDB:', error);
        return Promise.resolve(undefined);
      }
    }

    // 都没有找到
    return Promise.resolve(undefined);
  }

  /**
   * 删除缓存项
   * @param {string} key
   * @returns {Promise}
   */
  async delete(key) {
    // 从内存缓存中删除
    this.memoryCache.delete(key);

    // 如果使用 IndexedDB，也从 IndexedDB 中删除
    if (this.useIndexedDB && this.dbInitialized && this.db) {
      try {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          const request = store.delete(key);

          request.onsuccess = () => {
            resolve();
          };

          request.onerror = (event) => {
            console.warn('Failed to delete data from IndexedDB:', event.target.error);
            resolve(); // 即使 IndexedDB 失败也 resolve
          };
        });
      } catch (error) {
        console.warn('Error deleting data from IndexedDB:', error);
        return Promise.resolve();
      }
    }

    return Promise.resolve();
  }

  /**
   * 清空所有缓存
   * @returns {Promise}
   */
  async clear() {
    // 清空内存缓存
    this.memoryCache.clear();

    // 如果使用 IndexedDB，也清空 IndexedDB 中的数据
    if (this.useIndexedDB && this.dbInitialized && this.db) {
      try {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.storeName], 'readwrite');
          const store = transaction.objectStore(this.storeName);
          const request = store.clear();

          request.onsuccess = () => {
            resolve();
          };

          request.onerror = (event) => {
            console.warn('Failed to clear data from IndexedDB:', event.target.error);
            resolve(); // 即使 IndexedDB 失败也 resolve
          };
        });
      } catch (error) {
        console.warn('Error clearing data from IndexedDB:', error);
        return Promise.resolve();
      }
    }

    return Promise.resolve();
  }
}

// 导出默认实例
export default new CacheManager();
